import React, { useEffect, useState } from 'react';
import { useSchedule } from '../../contexts/ScheduleContext';
import { useLotto } from '../../contexts/LottoContext';
import { useSwap, SwapOffer } from '../../contexts/SwapContext';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Lotto } from '../../types/lotto';
import { getStats } from '../../utils/statsApi';

interface Stats {
  thisMonthRequests: number;
  activeLottos: number;
  totalLottoEntries: number;
  openOffers: number;
  matchedOffers: number;
  totalOffers: number;
  matchRate: number;
}

const StatsDashboard: React.FC = () => {
  const { dayOffRequests } = useSchedule();
  const { lottos } = useLotto();
  const { myOffers, allOffers } = useSwap();
  const { user } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);

  // activeLottos 필터링
  const activeLottos = lottos.filter((l: Lotto) => !l.isCompleted);
  
  // 전체 offers 합치기
  const offers = [...myOffers, ...allOffers];

  // 백엔드에서 통계 가져오기
  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await getStats();
        if (response.success) {
          setStats(response.data);
        }
      } catch (error) {
        console.error('통계 로드 실패:', error);
        // 에러 시 로컬 계산값 사용
      }
    };

    loadStats();
  }, []);

  // 통계 계산 (백엔드 실패 시 폴백)
  const thisMonthRequests = stats?.thisMonthRequests ?? dayOffRequests.filter(req => {
    const now = new Date();
    const reqDate = new Date(req.date);
    return reqDate.getMonth() === now.getMonth() && reqDate.getFullYear() === now.getFullYear();
  }).length;

  const activeLottosCount = stats?.activeLottos ?? activeLottos.length;

  const openOffersCount = stats?.openOffers ?? offers.filter((o: SwapOffer) => o.status === 'OPEN').length;
  const matchedOffersCount = stats?.matchedOffers ?? offers.filter((o: SwapOffer) => o.status === 'MATCHED').length;
  const totalOffers = stats?.totalOffers ?? offers.length;
  const matchRate = stats?.matchRate ?? (totalOffers > 0 ? ((matchedOffersCount / totalOffers) * 100).toFixed(0) : 0);

  const totalLottoEntries = stats?.totalLottoEntries ?? activeLottos.reduce((sum: number, lotto: Lotto) => sum + lotto.entries.length, 0);

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-primary flex items-center gap-2">
          📊 실시간 현황
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold">
            LIVE
          </div>
        </h2>
      </div>

      {/* 2열 그리드 (모바일에서도) */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* Fairness Point */}
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-4 border-2 border-yellow-300">
          <div className="flex items-center justify-between mb-2">
            <div className="text-2xl">⭐</div>
            <div className="bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 text-white rounded-full px-2 py-0.5 text-xs font-bold shadow-md">
              공정성
            </div>
          </div>
          <p className="text-3xl font-black text-yellow-600">{user?.fairnessPoints || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Fairness Point</p>
        </div>

        {/* 이번 달 휴무 신청 */}
        <Link 
          to="/scheduler"
          className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200 hover:border-blue-400 transition-all hover:scale-105 active:scale-95"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-2xl">📅</div>
            <div className="bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-md">
              월
            </div>
          </div>
          <p className="text-3xl font-black text-blue-600">{thisMonthRequests}</p>
          <p className="text-xs text-gray-500 mt-1">이번 달 휴무 신청</p>
        </Link>

        {/* 진행 중인 추첨 */}
        <Link
          to="/lotto"
          className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border-2 border-purple-200 hover:border-purple-400 transition-all hover:scale-105 active:scale-95"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-2xl">🎲</div>
            <div className="bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 text-white rounded-full px-2 py-0.5 text-xs font-bold shadow-md">
              {totalLottoEntries}명
            </div>
          </div>
          <p className="text-3xl font-black text-purple-600">{activeLottosCount}</p>
          <p className="text-xs text-gray-500 mt-1">진행 중인 추첨</p>
        </Link>

        {/* 교환 매칭 성공률 */}
        <Link
          to="/swap"
          className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200 hover:border-green-400 transition-all hover:scale-105 active:scale-95"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-2xl">🤝</div>
            <div className="bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 text-white rounded-full px-2 py-0.5 text-xs font-bold shadow-md">
              {openOffersCount}개
            </div>
          </div>
          <p className="text-3xl font-black text-green-600">{matchRate}%</p>
          <p className="text-xs text-gray-500 mt-1">교환 매칭률</p>
        </Link>
      </div>

      {/* 오늘의 인사이트 (컴팩트) */}
      <div className="bg-gradient-to-r from-primary to-accent text-white rounded-xl p-3 flex items-center gap-3">
        <span className="text-xl">💡</span>
        <div className="flex-1">
          <p className="text-xs font-bold mb-0.5">오늘의 인사이트</p>
          <p className="text-xs opacity-90">
            {activeLottosCount > 0 && `${activeLottosCount}건의 추첨이 진행 중이에요!`}
            {activeLottosCount === 0 && thisMonthRequests > 0 && `이번 달 ${thisMonthRequests}건의 휴무가 신청되었어요!`}
            {activeLottosCount === 0 && thisMonthRequests === 0 && openOffersCount > 0 && `${openOffersCount}건의 교환 제안이 대기 중이에요!`}
            {activeLottosCount === 0 && thisMonthRequests === 0 && openOffersCount === 0 && '새로운 활동을 시작해보세요!'}
          </p>
        </div>
        <span className="text-xl">✨</span>
      </div>
    </section>
  );
};

export default StatsDashboard;
